/* The four states of a list, and the dialogs both desks share.
 *
 * A list is reading, failed, empty, or full, and only the last one is a table.
 * The distinction that matters is the middle two: a read that failed is not a
 * zero, and an empty list says why it is empty rather than shrugging. Both rules
 * come from the handoff copy and both are easy to lose the first time somebody
 * writes `rows.length === 0 ? <Empty/> : <Table/>` and forgets there were four
 * answers.
 *
 * Every irreversible action goes through Confirm, which carries the verb on its
 * own button rather than saying OK.
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

/** Which of the four answers a fetch has come back with. */
export type FetchState = "reading" | "failed" | "ready";

/**
 * Escape closes, focus is trapped inside, and focus returns where it came from.
 *
 * Where focus lands on opening, in this order: a control the dialog already
 * focused itself (React's `autoFocus` runs before this effect, and taking focus
 * off it again was a bug, Bugbot #220), or one marked `data-autofocus`; then,
 * with `firstField`, the first field (input, select, textarea), then the first
 * control that is not marked `data-dialog-close`; otherwise, and as the last
 * resort, the first control. A dialog whose head is a Close button asks for
 * `firstField`, so Enter or Space on opening does not shut it.
 *
 * While any dialog is open, a rail it does not sit in is inert (Bugbot on
 * #220): the Admin's wash starts at the rail's edge and leaves it untinted
 * (A12), so it is in view but cannot be clicked or tabbed into. Escape and Tab
 * reach the top dialog wherever focus is, through the document, as well as
 * from inside it.
 *
 * The effect runs once on mount rather than on every change of `onClose`,
 * because re-running it would move focus to the first control again while
 * somebody was typing in the third. That means `onClose` has to be read through
 * a ref: closing over the first one would call a handler from a render several
 * states ago, which is the bug this arrangement exists to avoid.
 */
/** The dialogs open now, the last on top. */
const OPEN_DIALOGS: HTMLElement[] = [];

/** Make every rail no open dialog sits in inert while one is open, and put back what this set. */
function syncRails(): void {
  if (typeof document === "undefined") return;
  const on = OPEN_DIALOGS.length > 0;
  document.querySelectorAll<HTMLElement>(".rail").forEach((rail) => {
    const own = OPEN_DIALOGS.some((box) => rail.contains(box) || box.contains(rail));
    if (on && !own) {
      if (!rail.inert) {
        rail.inert = true;
        rail.setAttribute("data-dialog-inert", "");
      }
    } else if (rail.hasAttribute("data-dialog-inert")) {
      rail.inert = false;
      rail.removeAttribute("data-dialog-inert");
    }
  });
}

export function useDialog(
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  options: { firstField?: boolean } = {},
): void {
  const close = useRef(onClose);
  close.current = onClose;
  const firstField = options.firstField === true;
  // Where focus was before the dialog drew: read in the first render, because a
  // control inside that autofocuses has moved it by the time the effect runs.
  const opener = useRef<Element | null | undefined>(undefined);
  if (opener.current === undefined) opener.current = typeof document === "undefined" ? null : document.activeElement;

  useEffect(() => {
    const node = ref.current;
    if (node === null) return;

    const came = opener.current;
    const focusables = (): HTMLElement[] =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !(el as HTMLButtonElement).disabled && el.offsetParent !== null);

    const all = focusables();
    const already = document.activeElement instanceof HTMLElement && node.contains(document.activeElement) ? document.activeElement : null;
    const marked = all.find((el) => el.hasAttribute("data-autofocus"));
    const field = firstField ? all.find((el) => /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) : undefined;
    const notClose = firstField ? all.find((el) => !el.hasAttribute("data-dialog-close")) : undefined;
    const land = already ?? marked ?? field ?? notClose ?? all[0];
    if (land !== document.activeElement) land?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) return;
      const at = list.indexOf(document.activeElement as HTMLElement);
      const next = event.shiftKey
        ? at <= 0
          ? list.length - 1
          : at - 1
        : at === list.length - 1
          ? 0
          : at + 1;
      event.preventDefault();
      list[next]?.focus();
    };

    // Focus outside the box (on the page behind, or nowhere): the top dialog
    // still hears Escape and takes Tab back in.
    const onDocKey = (event: KeyboardEvent): void => {
      if (OPEN_DIALOGS[OPEN_DIALOGS.length - 1] !== node) return;
      if (event.target instanceof Node && node.contains(event.target)) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) return;
      event.preventDefault();
      (event.shiftKey ? list[list.length - 1] : list[0])?.focus();
    };

    OPEN_DIALOGS.push(node);
    syncRails();
    node.addEventListener("keydown", onKey);
    document.addEventListener("keydown", onDocKey);
    return () => {
      node.removeEventListener("keydown", onKey);
      document.removeEventListener("keydown", onDocKey);
      const at = OPEN_DIALOGS.lastIndexOf(node);
      if (at >= 0) OPEN_DIALOGS.splice(at, 1);
      syncRails();
      if (came instanceof HTMLElement) came.focus();
    };
  }, [ref]);
}

/**
 * A list that is being read.
 *
 * Rows rather than a spinner, because the shape of the answer is already known
 * and drawing it stops the page jumping when the answer lands.
 */
export function Loading({
  rows = 5,
  cols = 5,
  label = "Reading…",
}: {
  rows?: number;
  cols?: number;
  label?: string;
}): JSX.Element {
  const widths = [70, 40, 55, 30, 45];
  return (
    <div className="tablewrap" aria-busy="true" aria-live="polite">
      <div className="skel-head">{label}</div>
      {Array.from({ length: rows }).map((_, row) => (
        <div className="skel-row" key={row}>
          {Array.from({ length: cols }).map((__, col) => (
            <span className="skel" key={col} style={{ width: `${widths[col % widths.length]}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Nothing to show, and the reason it is nothing. Never a shrug. */
export function Empty({
  title,
  children,
  act,
  onAct,
}: {
  title: string;
  children?: ReactNode;
  act?: string;
  onAct?: () => void;
}): JSX.Element {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
      {act !== undefined ? (
        <button type="button" className="btn" onClick={onAct}>
          {act}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The read failed.
 *
 * The default wording says the thing the screen exists to say: this is not a
 * zero, and nothing below it has been drawn from half an answer.
 */
export function Failed({
  what = "That read did not come back",
  detail = "The service answered with an error, which is not the same as there being nothing to show. Nothing below is drawn from a partial answer.",
  onRetry,
}: {
  what?: string;
  detail?: string;
  // Spelled with `| undefined` because ListState forwards its own optional
  // handler straight through, and exactOptionalPropertyTypes tells the two
  // apart: "absent" and "present and undefined" are different types here.
  onRetry?: (() => void) | undefined;
}): JSX.Element {
  return (
    <div className="notice notice-bad" role="alert">
      <h3>{what}</h3>
      <p>{detail}</p>
      {onRetry !== undefined ? (
        <div className="acts acts-spaced">
          <button type="button" className="btn btn-quiet" onClick={onRetry}>
            Ask again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One dialog for anything that cannot be taken back.
 *
 * The confirm button carries the verb rather than saying OK, so the last thing
 * read before an irreversible action names the action. `needType` asks for a
 * word to be typed first, which is for the few that are worse than the rest.
 */
export function Confirm({
  title,
  verb,
  danger = false,
  children,
  onCancel,
  onConfirm,
  needType,
}: {
  title: string;
  verb: string;
  danger?: boolean;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  needType?: string;
}): JSX.Element {
  const box = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState("");
  useDialog(box, onCancel);
  const ready = needType === undefined || typed.trim().toUpperCase() === needType.toUpperCase();

  return (
    <div className="modal">
      <button type="button" className="scrim-bd" aria-label="Cancel" onClick={onCancel} />
      <div className="modal-box" role="dialog" aria-modal="true" aria-label={title} ref={box}>
        <h2 className="modal-t">{title}</h2>
        <div className="modal-body">{children}</div>
        {needType !== undefined ? (
          <label className="field field-spaced">
            <span className="label">Type {needType} to confirm</span>
            <input
              className="input"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
            />
          </label>
        ) : null}
        <div className="acts modal-acts">
          <button type="button" className="btn btn-quiet" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? "btn btn-quiet btn-danger" : "btn"}
            disabled={!ready}
            onClick={onConfirm}
          >
            {verb}
          </button>
        </div>
      </div>
    </div>
  );
}

/* The sortable column head that lived here (SortTh, with a 1 | -1 direction
 * nothing else used) is SortHead in page.tsx now, beside the sort state it
 * reads and the page layout whose right-hand column holds a list's search. One
 * head, one direction vocabulary ("asc" | "desc", the words a list endpoint
 * takes), and no second copy for a desk to drift onto. */

/** The first thing in the tab order, and the one a mouse never finds. */
export function SkipLink({ to = "stage" }: { to?: string }): JSX.Element {
  return (
    <a className="skip" href={`#${to}`}>
      Skip to the page
    </a>
  );
}

/**
 * Warn before losing a form.
 *
 * Returns the flag, a way to raise it, a guard to put in front of anything that
 * navigates, and a way to lower it again once the form has been saved.
 */
export function useUnsaved(
  onWarn: (go: () => void) => void,
): [boolean, () => void, (go: () => void) => void, () => void] {
  const [dirty, setDirty] = useState(false);
  const guard = (go: () => void): void => {
    if (!dirty) {
      go();
      return;
    }
    onWarn(go);
  };
  return [dirty, () => setDirty(true), guard, () => setDirty(false)];
}

/**
 * The four states of a list, in one place.
 *
 * The filters stay drawn in every one of them; only the answer below changes.
 * A filter bar that disappears while the page is reading is a filter bar that
 * loses what somebody just typed.
 */
export function ListState<Row>({
  state,
  rows,
  cols = 5,
  unit = "rows",
  onRetry,
  empty,
  children,
}: {
  state: FetchState;
  rows: readonly Row[] | null | undefined;
  cols?: number;
  unit?: string;
  onRetry?: (() => void) | undefined;
  empty: ReactNode;
  children: ReactNode;
}): JSX.Element {
  if (state === "reading") return <Loading rows={5} cols={cols} label={`Reading ${unit}…`} />;
  if (state === "failed") return <Failed onRetry={onRetry} />;
  if (rows === null || rows === undefined || rows.length === 0) return <>{empty}</>;
  return <>{children}</>;
}

/**
 * Make every `.tablescroll` that actually overflows reachable by keyboard.
 *
 * A wide table sits in a horizontally scrolling region, and a region a pointer
 * can scroll but a keyboard cannot reach is columns a keyboard user never sees
 * (WCAG 2.1.1; axe `scrollable-region-focusable`). Sixty-odd tables draw the
 * region as a bare div, so rather than every call site remembering a tabIndex,
 * one observer marks the ones that overflow, and unmarks them when a resize
 * makes them fit: a focus stop on a table that does not scroll is a tab press
 * that does nothing. The region is named after its table so a screen reader
 * says what it is.
 *
 * Call once at start-up; it returns the function that stops it.
 */
export function installScrollableRegions(root: Document = document): () => void {
  const mark = (): void => {
    for (const node of root.querySelectorAll<HTMLElement>(".tablescroll")) {
      const overflows = node.scrollWidth > node.clientWidth + 1;
      const marked = node.dataset.scrollRegion === "on";
      if (overflows && !marked) {
        node.dataset.scrollRegion = "on";
        node.tabIndex = 0;
        node.setAttribute("role", "region");
        const table = node.querySelector("table");
        const named = table?.getAttribute("aria-label") ?? table?.querySelector("caption")?.textContent?.trim();
        node.setAttribute("aria-label", named ? `${named}, scrolls sideways` : "Table, scrolls sideways");
      } else if (!overflows && marked) {
        delete node.dataset.scrollRegion;
        node.removeAttribute("tabindex");
        node.removeAttribute("role");
        node.removeAttribute("aria-label");
      }
    }
  };
  let queued = false;
  const soon = (): void => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      mark();
    });
  };
  const observer = new MutationObserver(soon);
  observer.observe(root.body, { childList: true, subtree: true });
  root.defaultView?.addEventListener("resize", soon);
  soon();
  return () => {
    observer.disconnect();
    root.defaultView?.removeEventListener("resize", soon);
  };
}
